import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { Card, FormGroup, InputWithUnit } from '../components/ui/Card';
import { AddressAutocomplete } from '../components/ui/AddressAutocomplete';
import { useProfiles } from '../hooks/useProfiles';
import { useUserSettings } from '../hooks/useUserSettings';
import { apiFetch } from '../lib/api';
import { geocodeAddress } from '../hooks/useGooglePlaces';
import { detectCounty } from '../lib/mn-data';
import { parseZillowUrl } from '../lib/zillow';
import type { BuyerProfile, Property } from '../lib/types';


const LAST_USED_KEY = 'lastUsedProfileId';

function targetProfile(
  profiles: BuyerProfile[],
  defaultProfileId: string | null,
): BuyerProfile | null {
  if (profiles.length === 0) return null;
  if (defaultProfileId) {
    const p = profiles.find(p => p.id === defaultProfileId);
    if (p) return p;
  }
  const lastUsed = localStorage.getItem(LAST_USED_KEY);
  if (lastUsed) {
    const p = profiles.find(p => p.id === lastUsed);
    if (p) return p;
  }
  return profiles[0];
}

function ShareContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();

  const sharedUrl   = searchParams.get('url')   ?? '';
  const sharedTitle = searchParams.get('title') ?? '';

  const parsed = useMemo(() => parseZillowUrl(sharedUrl), [sharedUrl]);

  const { profiles, loading: profilesLoading } = useProfiles(getToken);
  const { defaultProfileId, settingsLoading } = useUserSettings(getToken);

  // Form state — initialised from the parsed Zillow URL once available
  const [address,     setAddress]     = useState('');
  const [addressText, setAddressText] = useState('');
  const [city,        setCity]        = useState('');
  const [zip,         setZip]         = useState('');
  const [county,      setCounty]      = useState('');
  const [askingPrice, setAskingPrice] = useState(0);
  const [notes,       setNotes]       = useState('');
  const [nonMnWarning, setNonMnWarning] = useState(false);

  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [saving,      setSaving]      = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(true);

  // Pre-fill form from parsed Zillow URL (runs once when parsed is ready)
  useEffect(() => {
    if (!parsed) return;
    const initialAddress = parsed.address;
    setAddress(initialAddress);
    setAddressText(initialAddress);
    setCity(parsed.city);
    setZip(parsed.zip);
    setNonMnWarning(parsed.state !== 'MN');
    const detected = detectCounty(parsed.address, parsed.city, parsed.zip);
    if (detected) setCounty(detected.countyName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set the target profile once profiles + settings are loaded
  useEffect(() => {
    if (profilesLoading || settingsLoading || selectedProfileId) return;
    const target = targetProfile(profiles, defaultProfileId);
    if (target) setSelectedProfileId(target.id);
  }, [profilesLoading, settingsLoading, profiles, defaultProfileId, selectedProfileId]);

  const applyCountyDetection = (a: string, c: string, z: string) => {
    const detected = detectCounty(a, c, z);
    if (detected) setCounty(detected.countyName);
  };

  const handleSave = async () => {
    if (!selectedProfileId) return;
    if (askingPrice <= 0) {
      setGeocodeError('Please enter an asking price.');
      return;
    }
    const rawForGeocode = address.trim()
      ? [address.trim(), city.trim(), zip.trim()].filter(Boolean).join(', ')
      : addressText.trim();
    if (!rawForGeocode) {
      setGeocodeError('Please enter an address.');
      return;
    }
    setIsGeocoding(true);
    setGeocodeError(null);
    try {
      const normalized = await geocodeAddress(rawForGeocode);
      if (!normalized) {
        setGeocodeError('Address could not be verified. Please correct it and try again.');
        return;
      }
      const finalAddress = normalized.streetAddress;
      const finalCity    = normalized.city || city.trim();
      const finalZip     = normalized.zip  || zip.trim();
      setNonMnWarning(normalized.state !== 'MN');
      const finalCounty = county.trim() || (() => {
        const det = detectCounty(finalAddress, finalCity, finalZip);
        return det ? det.countyName : '';
      })();

      setSaving(true);
      const property = await apiFetch<Property>(
        `/profiles/${selectedProfileId}/properties`,
        {
          method: 'POST',
          body: JSON.stringify({
            address: finalAddress,
            city: finalCity,
            zip: finalZip,
            county: finalCounty,
            askingPrice,
            notes: notes.trim(),
          }),
        },
        getToken,
      );
      navigate(`/profiles/${selectedProfileId}/properties/${property.id}`);
    } catch (err: any) {
      setGeocodeError(err.message ?? 'Failed to save property.');
    } finally {
      setIsGeocoding(false);
      setSaving(false);
    }
  };

  // ── Not signed in ────────────────────────────────────────────────────────────
  if (!isSignedIn) {
    return (
      <div className="page-container">
        <h2 className="page-title">Save from Zillow</h2>
        <div className="note-box">
          Sign in to save this property to one of your buyer profiles.
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <Link to="/dashboard" className="btn btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  const dataLoading = profilesLoading || settingsLoading;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (dataLoading) {
    return (
      <div className="page-container">
        <h2 className="page-title">Save from Zillow</h2>
        <div className="empty-state"><div className="empty-state-text">Loading…</div></div>
      </div>
    );
  }

  // ── No profiles ──────────────────────────────────────────────────────────────
  if (profiles.length === 0) {
    return (
      <div className="page-container">
        <h2 className="page-title">Save from Zillow</h2>
        <div className="note-box">
          You need a buyer profile before you can save properties.
        </div>
        {sharedUrl && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.75rem', wordBreak: 'break-all' }}>
            Property URL saved — come back after creating a profile and paste it in.
          </div>
        )}
        <div style={{ marginTop: '1rem' }}>
          <Link to="/dashboard" className="btn btn-primary">Create a Profile →</Link>
        </div>
      </div>
    );
  }

  const isWorking = isGeocoding || saving;

  // ── Main form ────────────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      <h2 className="page-title" style={{ marginBottom: '0.25rem' }}>Save from Zillow</h2>
      {sharedTitle && (
        <p className="page-subtitle" style={{ marginBottom: '0.75rem' }}>
          {sharedTitle}
        </p>
      )}
      {!parsed && sharedUrl && (
        <div className="note-box" style={{ marginBottom: '1rem' }}>
          Couldn't auto-parse the listing details — fill in the address manually below.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* Profile selector */}
        {profiles.length > 1 && (
          <Card title="Save to Profile">
            <FormGroup label="Buyer Profile">
              <select
                value={selectedProfileId}
                onChange={e => setSelectedProfileId(e.target.value)}
              >
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </FormGroup>
          </Card>
        )}

        {/* Property details */}
        <Card title="Property Details">
          <FormGroup label="Address">
            <AddressAutocomplete
              initialValue={addressText}
              onLoadingChange={setPlacesLoading}
              onAddressTyped={value => {
                setAddressText(value);
                setAddress('');
                setGeocodeError(null);
              }}
              onPlaceSelected={(p, _fmt) => {
                setAddress(p.streetAddress);
                setAddressText(p.streetAddress);
                setCity(p.city);
                setZip(p.zip);
                setNonMnWarning(p.state !== 'MN');
                setGeocodeError(null);
                applyCountyDetection(p.streetAddress, p.city, p.zip);
              }}
            />
          </FormGroup>

          {nonMnWarning && (
            <div className="address-detected" style={{ background: '#fefce8', borderColor: '#fde68a', color: '#854d0e', marginBottom: '0.5rem' }}>
              <span className="detect-icon">⚠️</span>
              <span>Outside Minnesota — county and tax rate won't be auto-detected.</span>
            </div>
          )}
          {geocodeError && (
            <div className="address-detected" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginBottom: '0.5rem' }}>
              <span className="detect-icon">✕</span>
              <span>{geocodeError}</span>
            </div>
          )}

          <div className="form-row">
            <FormGroup label="City">
              <input
                type="text"
                value={city}
                onChange={e => { setCity(e.target.value); applyCountyDetection(address, e.target.value, zip); }}
                placeholder="Minneapolis"
              />
            </FormGroup>
            <FormGroup label="ZIP Code">
              <input
                type="text"
                value={zip}
                onChange={e => { setZip(e.target.value); applyCountyDetection(address, city, e.target.value); }}
                placeholder="55401"
                inputMode="numeric"
                maxLength={5}
              />
            </FormGroup>
          </div>

          <FormGroup label="County">
            <input
              type="text"
              value={county}
              onChange={e => setCounty(e.target.value)}
              placeholder="Hennepin"
            />
          </FormGroup>

          <FormGroup label="Asking Price">
            <InputWithUnit
              unit="$"
              value={askingPrice || ''}
              onChange={setAskingPrice}
              min={0}
              step={1000}
            />
          </FormGroup>

          <FormGroup label="Notes (optional)">
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Open house Sunday, backs to park…"
            />
          </FormGroup>
        </Card>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <Link to="/dashboard" className="btn btn-secondary">Cancel</Link>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isWorking || placesLoading || !selectedProfileId}
          >
            {isGeocoding ? 'Verifying address…' : saving ? 'Saving…' : 'Save Property'}
          </button>
        </div>

      </div>
    </div>
  );
}

export function SharePage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Save from Zillow</h2>
        <div className="note-box">
          Authentication is required to save properties.
        </div>
      </div>
    );
  }
  return <ShareContent />;
}
